import type { LogFn } from '../log.js';
import { formatWebSocketClose } from '../ws-close-diagnostics.js';
import { RECONNECT_QUIET_MS } from './constants.js';

/** Shared quiet-window + verbose reconnect logging state (main bridge, firehose, or pending auth). */
export interface ReconnectQuietSlot {
  timer: ReturnType<typeof setTimeout> | null;
  verboseLogs: boolean;
  pendingCloseLog: { code: number; reason: string } | null;
}

export function createEmptyReconnectQuietSlot(): ReconnectQuietSlot {
  return { timer: null, verboseLogs: false, pendingCloseLog: null };
}

export function clearReconnectQuietTimer(quiet: ReconnectQuietSlot): void {
  if (quiet.timer != null) {
    clearTimeout(quiet.timer);
    quiet.timer = null;
  }
}

/** Clears a pending quiet-window disconnect log without emitting it (used after silent auto-reconnect). */
export function abandonReconnectQuietWindow(quiet: ReconnectQuietSlot): void {
  clearReconnectQuietTimer(quiet);
  quiet.pendingCloseLog = null;
  quiet.verboseLogs = false;
}

/**
 * On intentional shutdown or successful reconnect: cancel deferred disconnect noise and optionally log recovery.
 */
export function clearReconnectQuietOnSuccessfulConnection(
  quiet: ReconnectQuietSlot,
  log: LogFn,
  reconnectedMessage: string,
): void {
  clearReconnectQuietTimer(quiet);
  quiet.pendingCloseLog = null;
  if (quiet.verboseLogs) {
    log(reconnectedMessage);
    quiet.verboseLogs = false;
  }
}

/**
 * If not reconnecting: log disconnect immediately. Otherwise arm a quiet window; only log if the outage persists.
 */
export function beginDeferredDisconnectForReconnect(options: {
  isClosedByUser: () => boolean;
  quiet: ReconnectQuietSlot;
  code: number;
  reason: string;
  willReconnect: boolean;
  log: LogFn;
  serviceLabel: string;
  shutdownDetail: string;
  reconnectingDetail: string;
  quietMs?: number;
  /** When the quiet timer fires: if true, omit disconnect log and do not enable verbose attempt logs. */
  shouldAbortQuietWindow: () => boolean;
  /**
   * When true with `willReconnect`: do not log disconnect / reconnect lines (tier-based logs handle UX).
   */
  silentWhileReconnect?: boolean;
}): void {
  const {
    isClosedByUser,
    quiet,
    code,
    reason,
    willReconnect,
    log,
    serviceLabel,
    shutdownDetail,
    reconnectingDetail,
    quietMs = RECONNECT_QUIET_MS,
    shouldAbortQuietWindow,
    silentWhileReconnect = false,
  } = options;

  if (!willReconnect) {
    log(formatWebSocketClose(serviceLabel, code, reason, shutdownDetail));
    return;
  }
  if (silentWhileReconnect) {
    return;
  }
  quiet.pendingCloseLog = { code, reason };
  if (quiet.timer == null) {
    quiet.timer = setTimeout(() => {
      quiet.timer = null;
      if (isClosedByUser()) return;
      if (shouldAbortQuietWindow()) return;
      if (quiet.pendingCloseLog) {
        const { code: c, reason: r } = quiet.pendingCloseLog;
        quiet.pendingCloseLog = null;
        log(formatWebSocketClose(serviceLabel, c, r, reconnectingDetail));
      }
      quiet.verboseLogs = true;
    }, quietMs);
  }
}
